import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  StudyModel,
  type StudyPermissionModel,
  LoadingStateModel,
  type StudyImportDiff,
  type INewStudy,
  type StudySchemaExport,
  DEFAULT_CASE_TEAM_LABEL,
  DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL
} from '@/models';
import StudyService from '@/services/studyService';
import { LocalStorageUtils, routeConstants } from '@/utils';
import { useRoute, useRouter } from 'vue-router';
import { useDashboardStore } from './dashboard';
import { useEntryListStore } from './entryList';
import { useFormStore } from './forms';
import { usePipelineStore } from './pipeline';


export const useStudyStore = defineStore('study', () => {
  const router = useRouter();
  const route = useRoute();
  const dashboardStore = useDashboardStore();
  const entryListStore = useEntryListStore();
  const formStore = useFormStore();
  const pipelineStore = usePipelineStore();

  const studies = ref<StudyModel[]>([]);
  const studyLoader = ref(new LoadingStateModel({isLoading: false}));
  const selectedStudy = ref<StudyModel>();
  const selectedStudyId = ref<string>();
  const studyPermissions = ref<StudyPermissionModel>();

  /**
   * Whether this study runs the Perfusion Companion app. Off unless the flag says otherwise, so a
   * study that has not been set up for it shows none of the scan features.
   */
  const isPerfusionCompanionStudy = computed(
    () => selectedStudy.value?.flags?.perfusionCompanion === true
  );

  /**
   * The study as the Perfusion Companion endpoints name it: `:id` on each of them. Empty with no
   * study selected, which the scans panel reads as nothing to load.
   */
  const pumpCenterId = computed(
    () => selectedStudyId.value ?? selectedStudy.value?.identifier ?? ''
  );

  /**
   * Whether this study runs the per-case team roster. Off unless the flag says
   * otherwise, so a study that has not been set up for it keeps the one-click
   * case creation it has always had and shows none of the roster UI.
   */
  const isCaseTeamEnabled = computed(
    () => selectedStudy.value?.flags?.caseTeam?.enable === true
  );

  /** The study's role list and labels, or undefined when the flag is off. */
  const caseTeamConfig = computed(() =>
    isCaseTeamEnabled.value ? selectedStudy.value?.flags?.caseTeam : undefined
  );

  /** Section heading, falling back to the neutral platform wording. */
  const caseTeamLabel = computed(
    () => caseTeamConfig.value?.label?.trim() || DEFAULT_CASE_TEAM_LABEL
  );

  const caseTeamSubscribersLabel = computed(
    () =>
      caseTeamConfig.value?.subscribersLabel?.trim() ||
      DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL
  );

  /**
   * Whether this study has case notification rules switched on.
   *
   * Note this says the rules are *configured*, not that anything sends them —
   * no evaluator reads the flag yet. It exists so the roster UI can tell the two
   * situations apart: a case team with notifications behind it means something
   * different to the person muting a member than one without.
   */
  const isCaseNotificationsEnabled = computed(
    () => selectedStudy.value?.flags?.caseNotifications?.enable === true
  );

  /** The study's rules and anchors, or undefined when the flag is off. */
  const caseNotificationsConfig = computed(() =>
    isCaseNotificationsEnabled.value ?
      selectedStudy.value?.flags?.caseNotifications :
      undefined
  );


  /**
   * The app's first load. This had no try/catch at all, and neither did its callers, so a failure here
   * was an unhandled rejection that left `StudyFetchContainer` spinning forever with nothing on screen
   * — the symptom this ticket describes. A failure is now fatal-tier: the error screen replaces the
   * outlet, with retry wired back to this function.
   */
  const getStudies = async () => {
    studyLoader.value.startLoading();
    // Before the list is asked for: the dashboard's views read the selected study from their first
    // render. The forms read used to select the routed study on its way through, and on a refreshed
    // URL it was the first to; nothing but this store selects one now.
    if (route.params.studyIdentifier) {
      selectedStudyId.value = route.params.studyIdentifier as string;
    }
    try {
      studies.value = await StudyService.getStudies();

      studies.value.sort((a, b) => new Date(b.creation).getTime() - new Date(a.creation).getTime());

      if (studies.value.length === 0) {
        selectedStudy.value = undefined;
        selectedStudyId.value = undefined;
        dashboardStore.setUserOptionLinks();
        return;
      }

      if (route.params.studyIdentifier) {
        selectedStudyId.value = route.params.studyIdentifier as string;
        selectedStudy.value = await StudyService.getStudy(route.params.studyIdentifier as string);
      } else {
        const firstStudyId = studies.value[0].identifier;
        selectedStudy.value = await StudyService.getStudy(firstStudyId);
        selectedStudyId.value = firstStudyId;

        //set selected study in the cache
        LocalStorageUtils.setStudyDetails(selectedStudy.value);
      }

      studyPermissions.value = await StudyService.getStudyPermissions(selectedStudy.value!.identifier);
      dashboardStore.setDashboardLinks();
    } catch (error) {
      // Not rethrown: the caller's `if (!selectedStudyId)` branch falls through harmlessly while the
      // error screen renders over it.
      studyLoader.value.setFatalError(error, () => getStudies());
    } finally {
      // Always runs now — the old code only reached `stopLoadingAndFinish` on the happy path.
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const getStudy = async (identifier: string) => {
    try {
      selectedStudy.value = await StudyService.getStudy(identifier);
      selectedStudyId.value = identifier;

      //set selected study in the cache
      LocalStorageUtils.setStudyDetails(selectedStudy.value);
    } catch (error) {
      studyLoader.value.setFatalError(error, () => getStudy(identifier));
    }
  };

  const setStudyFromCache = () => {
    const studyId = LocalStorageUtils.getStudyIdentifier();
    if (studyId) {
      selectedStudyId.value = studyId;
    }
  };

  const setStudy = async (study: StudyModel) => {
    //unselect selected case
    studyLoader.value.startLoading();
    try {
      entryListStore.selectedCase = undefined;
      selectedStudy.value = await StudyService.getStudy(study.identifier);
      LocalStorageUtils.setStudyDetails(selectedStudy.value);
      selectedStudyId.value = study.identifier;
      // Started with the selection: until it starts, the forms list still holds the study just left.
      // It settles its own failure, so a failed permissions read below leaves it running.
      const formsRead = formStore.getStudyForms(study.identifier);

      studyPermissions.value = await StudyService.getStudyPermissions(selectedStudy.value!.identifier);
      dashboardStore.setDashboardLinks();

      await formsRead;
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const studyImportDiff = ref<StudyImportDiff>();
  const importExportLoader = ref(new LoadingStateModel({ isLoading: false }));
  const importAndValidateStudy = async (file: File) => {
    importExportLoader.value.startLoading();
    try {
      studyImportDiff.value = await StudyService.importAndValidate(
        selectedStudy.value!.identifier,
        file,
        selectedStudy.value?.flags?.customDataDictionary
      );
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const getExport = async (payload: StudySchemaExport) => {
    importExportLoader.value.startLoading();
    try {
      await StudyService.getStudyExport(selectedStudy.value!.identifier, payload);
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const importAndApproveStudy = async () => {
    importExportLoader.value.startLoading();
    try {
      await StudyService.importStudy(selectedStudy.value!.identifier, studyImportDiff.value!);
      importExportLoader.value.setSuccess('Study updated successfully');
      formStore.getStudyForms(selectedStudy.value!.identifier);
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const createStudy = async (
    newStudy: Omit<INewStudy, 'organizationIdentifier'>,
    redirect: boolean = true
  ): Promise<string | undefined> => {
    studyLoader.value.startLoading();
    try {
      const { data } = await StudyService.postStudy({
        ...newStudy,
        organizationIdentifier: selectedStudy.value?.organizationIdentifier
      });
      const createdStudyIdentifier = data.hexIdentifier || data.identifier;
      await getStudies();
      await setStudy({ ...data, identifier: createdStudyIdentifier });
      pipelineStore.resetStore();
      if (redirect && createdStudyIdentifier) {
        await router.push({
          name: routeConstants.FORMS.name,
          params: { studyIdentifier: createdStudyIdentifier }
        });
      }
      return createdStudyIdentifier;
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const importStudyForm = async (fileName: string, filepath: string) => {
    studyLoader.value.startLoading();
    try {
      await StudyService.importStudyForm(selectedStudy.value!.identifier, fileName, filepath);
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  return {
    studies,
    selectedStudy,
    selectedStudyId,
    studyPermissions,
    isPerfusionCompanionStudy,
    pumpCenterId,
    isCaseTeamEnabled,
    caseTeamConfig,
    caseTeamLabel,
    caseTeamSubscribersLabel,
    isCaseNotificationsEnabled,
    caseNotificationsConfig,
    studyLoader,
    importExportLoader,
    studyImportDiff,
    getStudies,
    getStudy,
    setStudyFromCache,
    setStudy,
    createStudy,
    importAndValidateStudy,
    importAndApproveStudy,
    getExport,
    importStudyForm
  };
});
